 import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

// Enhanced photo carried from the homepage demo widget into the create flow so
// users don't start from scratch after unlocking their photo.
export interface PendingDemoPhoto {
  /** Local blob URL of the user's original upload — only valid within the same document. */
  originalUrl?: string;
  /** Remote URL of the clean (unlocked) enhanced photo. */
  enhancedUrl: string;
}

 interface CreateListingContextType {
   shouldOpenCreate: boolean;
   triggerCreate: () => void;
   resetCreate: () => void;
  pendingImportUrl: string | null;
  setPendingImportUrl: (url: string | null) => void;
  consumePendingImportUrl: () => string | null;
  setPendingDemoPhoto: (photo: PendingDemoPhoto | null) => void;
  consumePendingDemoPhoto: () => PendingDemoPhoto | null;
 }

 const CreateListingContext = createContext<CreateListingContextType | null>(null);

const PENDING_DEMO_PHOTO_KEY = 'listbright_pending_demo_photo';

 export function CreateListingProvider({ children }: { children: ReactNode }) {
   const [shouldOpenCreate, setShouldOpenCreate] = useState(false);
  const [pendingImportUrl, setPendingImportUrlState] = useState<string | null>(null);
  const [pendingDemoPhoto, setPendingDemoPhotoState] = useState<PendingDemoPhoto | null>(null);

   const triggerCreate = useCallback(() => {
     setShouldOpenCreate(true);
    // Also set a session storage flag for cross-page navigation
    sessionStorage.setItem('listbright_create_pending', 'true');
   }, []);

   const resetCreate = useCallback(() => {
     setShouldOpenCreate(false);
    sessionStorage.removeItem('listbright_create_pending');
   }, []);

  const setPendingImportUrl = useCallback((url: string | null) => {
    setPendingImportUrlState(url);
    if (url) {
      sessionStorage.setItem('listbright_pending_import_url', url);
    } else {
      sessionStorage.removeItem('listbright_pending_import_url');
    }
  }, []);

  const consumePendingImportUrl = useCallback(() => {
    const url = sessionStorage.getItem('listbright_pending_import_url');
    if (url) {
      sessionStorage.removeItem('listbright_pending_import_url');
      setPendingImportUrlState(null);
    }
    return url;
  }, []);

  const setPendingDemoPhoto = useCallback((photo: PendingDemoPhoto | null) => {
    setPendingDemoPhotoState(photo);
    try {
      if (photo) {
        // Blob URLs die with the document, so only the remote clean URL is
        // persisted; the in-memory state keeps the original for SPA navigation.
        sessionStorage.setItem(
          PENDING_DEMO_PHOTO_KEY,
          JSON.stringify({ enhancedUrl: photo.enhancedUrl }),
        );
      } else {
        sessionStorage.removeItem(PENDING_DEMO_PHOTO_KEY);
      }
    } catch {
      // sessionStorage unavailable (private mode) — in-memory state still works
    }
  }, []);

  const consumePendingDemoPhoto = useCallback((): PendingDemoPhoto | null => {
    let photo = pendingDemoPhoto;
    if (!photo) {
      try {
        const raw = sessionStorage.getItem(PENDING_DEMO_PHOTO_KEY);
        photo = raw ? (JSON.parse(raw) as PendingDemoPhoto) : null;
      } catch {
        photo = null;
      }
    }
    if (photo) {
      setPendingDemoPhotoState(null);
      sessionStorage.removeItem(PENDING_DEMO_PHOTO_KEY);
    }
    return photo?.enhancedUrl ? photo : null;
  }, [pendingDemoPhoto]);

  // Check for pending create on mount (handles page navigation)
  useEffect(() => {
    const pending = sessionStorage.getItem('listbright_create_pending');
    if (pending === 'true') {
      setShouldOpenCreate(true);
    }
    const url = sessionStorage.getItem('listbright_pending_import_url');
    if (url) setPendingImportUrlState(url);
  }, []);

   return (
    <CreateListingContext.Provider value={{ shouldOpenCreate, triggerCreate, resetCreate, pendingImportUrl, setPendingImportUrl, consumePendingImportUrl, setPendingDemoPhoto, consumePendingDemoPhoto }}>
       {children}
     </CreateListingContext.Provider>
   );
 }

 export function useCreateListing() {
   const context = useContext(CreateListingContext);
   if (!context) {
     throw new Error('useCreateListing must be used within CreateListingProvider');
   }
   return context;
 }
